import environment from '../config/environment';

const defaultAnalyticsData = {
  // Overview metrics
  totalVisitors: 0,
  visitorChange: 0,
  avgSessionDuration: '0m',
  durationChange: 0,
  conversionRate: 0,
  conversionChange: 0,
  totalDonations: '$0',
  donationChange: 0,

  // Visitor Analytics
  visitorAnalytics: {
    visitorTrends: [],
    geographicData: [],
    newVsReturning: { new: 0, returning: 0 },
    totalVisitors: 0,
    averageTimeOnSite: '0m',
    bounceRate: '0%',
    visitorChange: 0,
    timeChange: 0,
    bounceChange: 0
  },

  // Page Analytics
  pageAnalytics: {
    pageViews: [],
    popularPages: [],
    exitPages: [],
    averageTimeOnPage: '0s'
  },

  // Resource Analytics
  resourceAnalytics: {
    downloadsByResource: [],
    downloadsByCategory: [],
    viewTimeByResource: [],
    searchTerms: [],
    resourceEngagement: [],
    categoryViews: []
  },

  // Application Analytics
  applicationAnalytics: {
    applicationSubmissions: [],
    applicationsByType: [],
    completionRates: {
      started: 0,
      inProgress: 0,
      completed: 0,
      abandoned: 0
    },
    averageCompletionTime: '0m',
    commonDropoffPoints: []
  },

  // Academy Analytics
  academyAnalytics: {
    resourceDownloads: [],
    popularResources: [],
    resourcesByCategory: [],
    readingStats: {
      averageReadTime: '0m',
      completionRate: '0%',
      totalDownloads: 0
    }
  },

  // Donation Analytics
  donationAnalytics: {
    donationTrends: [],
    donationsByType: [],
    donorRetention: {
      newDonors: 0,
      returningDonors: 0,
      retentionRate: '0%'
    },
    averageDonation: '$0',
    totalDonations: '$0',
    donorGrowth: 0
  },

  // Engagement Metrics
  engagement: {
    'Avg. Time on Site': '0m',
    'Pages per Session': '0',
    'Bounce Rate': '0%',
    'Return Rate': '0%'
  },

  // Conversion Funnel
  funnelData: [
    { label: 'Visitors', value: 0 },
    { label: 'Engaged', value: 0 },
    { label: 'Converted', value: 0 }
  ],

  // Recent Activity
  recentVisitors: [],
  recentActions: [],
  recentDownloads: []
};

class AnalyticsService {
  constructor() {
    this.apiUrl = environment().apiUrl;
    this.sessionId = this.generateSessionId();
    this.queue = [];
    this.isProcessing = false;
    
    // Process queue every 10 seconds
    setInterval(() => this.processQueue(), 10000);
    
    // Track page load
    this.trackPageView({
      path: window.location.pathname,
      title: document.title,
      referrer: document.referrer
    });
    
    // Add event listeners
    this.addEventListeners();
  }
  
  generateSessionId() {
    return 'session_' + Math.random().toString(36).substring(2, 15);
  }
  
  addEventListeners() {
    // Track clicks on important elements
    document.addEventListener('click', (e) => {
      const target = e.target.closest('a, button');
      if (!target) return;
      
      // Don't track analytics UI interactions
      if (target.closest('.analytics-dashboard')) return;
      
      const type = target.tagName.toLowerCase();
      const text = target.innerText || target.textContent;
      const href = target.href || '';
      const id = target.id || '';
      const classes = Array.from(target.classList).join(' ');
      
      this.trackEvent({
        type: 'click',
        element: type,
        text: text?.substring(0, 50),
        href,
        id,
        classes
      });
    });
    
    // Track form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form || form.closest('.analytics-dashboard')) return;
      
      this.trackEvent({
        type: 'form_submit',
        formId: form.id || '',
        formAction: form.action || '',
        formMethod: form.method || ''
      });
    });
  }
  
  trackPageView(data) {
    this.queueEvent({
      type: 'pageview',
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      ...data
    });
  }
  
  trackEvent(data) {
    this.queueEvent({
      type: data.type || 'custom',
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      ...data
    });
  }
  
  queueEvent(event) {
    this.queue.push(event);
    
    // If queue gets too large, process immediately
    if (this.queue.length >= 10) {
      this.processQueue();
    }
  }
  
  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) return;
    
    this.isProcessing = true;
    const events = [...this.queue];
    this.queue = [];
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${this.apiUrl}/api/analytics/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ events }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        // If server error, put events back in queue
        this.queue = [...events, ...this.queue];
        console.warn('Failed to send analytics events, will retry later');
      }
    } catch (error) {
      // If network error, put events back in queue
      this.queue = [...events, ...this.queue];
      console.warn('Failed to send analytics events:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  // Validate endpoint on initialization
  async initialize() {
    if (!this.apiUrl) {
      console.warn('Analytics: API URL not configured');
      return;
    }

    try {
      // Test the connection
      const response = await fetch(`${this.apiUrl}/api/analytics/ping`);
      if (!response.ok) {
        throw new Error(`Analytics endpoint not available: ${response.status}`);
      }
      console.log('Analytics service initialized successfully');
    } catch (error) {
      console.warn('Analytics service initialization failed:', error);
      // Store any events that fail during initialization
      this.failedEvents = [];
    }
  }

  // Add method to handle failed events
  async retryFailedEvents() {
    if (this.failedEvents.length === 0) return;

    const events = [...this.failedEvents];
    this.failedEvents = [];

    for (const event of events) {
      try {
        await this.trackEvent(event);
      } catch (error) {
        this.failedEvents.push(event);
      }
    }
  }

  // Session management methods
  createNewSession() {
    const sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('analytics_session_id', sessionId);
    return sessionId;
  }

  getSessionId() {
    return sessionStorage.getItem('analytics_session_id');
  }

  getUserId() {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.id || 'anonymous';
  }


  // Failed events queue management
  queueFailedEvent(eventData) {
    this.failedEvents.push({
      event: eventData,
      timestamp: new Date(),
      retryCount: 0
    });
    localStorage.setItem('failedAnalyticsEvents', JSON.stringify(this.failedEvents));
  }

  // Session data getter
  getSessionData() {
    return {
      sessionId: this.getSessionId(),
      userId: this.getUserId(),
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }

  // Portal engagement tracking
  async trackPortalEngagement(data) {
    return this.trackEvent({
      type: 'portal_engagement',
      category: 'portal',
      ...data,
      timestamp: new Date(),
      sessionData: this.getSessionData(),
      metadata: {
        section: data.section,
        action: data.action,
        documentAccessed: data.document,
        reportViewed: data.report,
        communicationMethod: data.communication,
        taskCompleted: data.task,
        timeInPortal: data.duration,
        successfulActions: data.successes,
        failedActions: data.failures,
        securityEvents: data.security
      }
    });
  }

  // Get auth token
  getAuthToken() {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user?.token) {
      console.warn('Analytics: No auth token found');
      return '';
    }
    return user.token;
  }

  // Initialize WebSocket connection for real-time updates
  initializeRealTime() {
    if (process.env.NODE_ENV === 'development') {
      console.log('Skipping WebSocket initialization in development');
      return;
    }

    this.socket = new WebSocket(process.env.REACT_APP_WS_URL);
    
    this.socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      this.handleRealTimeUpdate(data);
    };

    this.socket.onclose = () => {
      console.log('WebSocket closed. Attempting to reconnect...');
      setTimeout(() => this.initializeRealTime(), 5000);
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  handleRealTimeUpdate(data) {
    const event = new CustomEvent('analytics-update', { detail: data });
    window.dispatchEvent(event);
  }

  // Add this method to your AnalyticsService class
  async getDashboardData(timeframe) {
    try {
      const endpoint = `${this.apiUrl}/api/analytics/dashboard`;
      console.log('Attempting to connect to:', endpoint);

      const response = await fetch(`${endpoint}?timeframe=${timeframe}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const data = await response.json();
      return {
        ...defaultAnalyticsData,
        ...data
      };
    } catch (error) {
      console.error('Analytics fetch error:', error);
      if (!navigator.onLine) {
        throw new Error('No internet connection');
      }
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Cannot connect to analytics server - please check server status');
      }
      throw error;
    }
  }

  // Separate mock data for cleaner code
  getMockData() {
    return {
      totalVisitors: '1,234',
      visitorChange: 12.5,
      avgSessionDuration: '3m 45s',
      durationChange: 5.2,
      conversionRate: 2.8,
      conversionChange: 0.5,
      totalDonations: '$12,345',
      donationChange: 15.7,
      trafficData: [],
      pageViews: [],
      deviceData: [],
      clickData: [],
      engagement: {
        'Avg. Time on Site': '4m 12s',
        'Pages per Session': '3.2',
        'Bounce Rate': '45%',
        'Return Rate': '22%'
      },
      funnelData: [
        { label: 'Visitors', value: 1000 },
        { label: 'Engaged', value: 750 },
        { label: 'Converted', value: 250 }
      ],
      recentVisitors: []
    };
  }

  // Add this method for production error logging
  async logError(error, context) {
    if (process.env.NODE_ENV === 'production') {
      try {
        await fetch(`${this.apiUrl}/api/analytics/errors`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.getAuthToken()}`
          },
          body: JSON.stringify({
            error: error.message,
            stack: error.stack,
            context,
            timestamp: new Date(),
            sessionData: this.getSessionData()
          })
        });
      } catch (e) {
        console.error('Failed to log error:', e);
      }
    }
  }

  trackResourceInteraction(resourceId, resourceTitle, interactionType) {
    try {
      // Your analytics tracking logic here
      console.log('Resource interaction tracked:', { resourceId, resourceTitle, interactionType });
    } catch (error) {
      console.error('Analytics tracking error:', error);
      // Don't throw the error - fail silently to prevent breaking the download
    }
  }
}

// Create and export a singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService; 